/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-runloop, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed, set, get } from '@ember/object';
import { alias, setDiff } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { next } from '@ember/runloop';
import EnrolmentStatus from 'linced-app/enums/enrolment-status';
import InputType from '@linc-technologies/linced-common/enums/input-type';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import JurisdictionalStatus from 'linced-app/enums/jurisdictional-status';
import { loadRecordsInChunks } from 'linced-app/utils/api';
import { getRequiredFieldsForJurisdiction } from 'linced-app/utils/fields';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';
import config from 'linced-app/config/environment';
import moment from 'moment';

export default class PendingController extends Controller {
  // Services
  @service
  fetch;

  @service
  options;

  @service
  session;

  @service
  store;

  EnrolmentStatus = EnrolmentStatus;
  Jurisdiction = Jurisdiction;
  @tracked
  selected = [];

  @computed('selected.[]')
  get selectedLearners() {
    return this.pre.filter((learner) => {
      return this.selected.includes(learner.id);
    });
  }

  // Disable ENROL arrival/leaver uploads unless specifically enabled
  // for the school, to facilitate approval testing without full release.
  @optionAlias('hero:nzMOE:enrolArrivalsLeaversEnabled')
  nzMoeEnrolArrivalsLeaversEnabled;

  @alias('model.learners')
  pre;

  @alias('session.school.jurisdiction')
  jurisdiction;

  @computed('jurisdiction', 'model.learnerFields')
  get requiredFields() {
    let fields = getRequiredFieldsForJurisdiction(this.jurisdiction);
    // Remove current year level from required fields.
    // This means that year level 0 will be allowed; this may need to be jurisdictionally limited.
    let yearLevelField = fields.find(
      (item) => item.key === this.yearLevelFieldKey,
    );
    if (!isEmpty(yearLevelField)) {
      fields = fields.filter((field) => field !== yearLevelField);
    }

    if (isEmpty(fields.find((item) => item.key === 'core:dob'))) {
      fields = [
        ...fields,
        {
          key: 'core:dob',
          error: 'Please check the Date of Birth',
        },
      ];
    }

    fields.forEach((field) => {
      set(
        field,
        'field',
        this.model.learnerFields.find((item) => item.key === field.key),
      );
    });

    return fields;
  }

  @computed('model.learnerFields.@each.inputType')
  get jurisdictionalIdField() {
    return this.model.learnerFields.find(
      (item) => item.inputType === InputType.JURISDICTIONAL_ID,
    );
  }

  @computed('jurisdictionalIdField', 'jurisdiction')
  get jurisdictionalIdLabel() {
    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        return 'NSN';
      default:
        return this.get('jurisdictionalIdField.label');
    }
  }

  @computed('jurisdiction')
  get yearLevelFieldKey() {
    switch (this.jurisdiction) {
      case Jurisdiction.NZ_MOE:
        return 'nzMOE:currentYearLevel';
      default:
        return 'core:currentYearLevel';
    }
  }

  @computed('yearLevelFieldKey', 'model.learnerFields.@each.key')
  get yearLevelField() {
    return this.model.learnerFields.find(
      (item) => item.key === this.yearLevelFieldKey,
    );
  }

  // enrolments is a native getter rather than a computed. `learner` is a nested
  // object on each pre-enrolment, and a two-level @each key is not allowed in
  // Ember.
  get enrolments() {
    let enrolments = [];
    this.pre.forEach((pre) => {
      if (!isEmpty(get(pre, 'learner.enrolmentId'))) {
        let enrolment = this.store.peekRecord(
          'people/enrolment',
          get(pre, 'learner.enrolmentId'),
        );
        if (!isEmpty(enrolment)) {
          enrolments.push(enrolment);
        }
      }
    });
    return enrolments;
  }

  @computed(
    'pre.@each.{id,homeroom,hasDirtyAttributes}',
    'requiredFields.[]',
    'enrolments.@each.entryDate',
  )
  get ready() {
    let now = moment();
    return this.pre.filter((person) => {
      let isSaved = !person.hasDirtyAttributes;
      let hasHomeroom = !isEmpty(person.homeroom);
      let enrolment = this.store.peekRecord(
        'people/enrolment',
        get(person, 'learner.enrolmentId'),
      );
      let hasStartDate = !isEmpty(enrolment) && !isEmpty(enrolment.entryDate);
      let hasStarted =
        hasStartDate && moment(enrolment.entryDate).isSameOrBefore(now, 'd');
      return (
        isSaved &&
        hasHomeroom &&
        hasStarted &&
        !this.requiredFields.some((field) => {
          return isEmpty(person.get(`fields.${field.key}.value`));
        })
      );
    });
  }

  @setDiff('pre', 'ready')
  toCorrect;

  startDateValidation = Object.freeze([
    {
      message:
        'Students cannot be fully enrolled until their first attendance date',
      validate: (val) => {
        let parsed = moment(val);
        let now = moment();
        return parsed.isValid() && parsed.isSameOrBefore(now, 'd');
      },
    },
  ]);

  @tracked
  processing = false;
  @tracked
  processingDialogOpen = false;
  @tracked numProcessed = 0;

  @tracked
  uploaded;

  @computed('numProcessed', 'selectedLearners.length')
  get fractionProcessed() {
    if (this.numProcessed) {
      return (this.numProcessed / this.selectedLearners.length) * 100;
    }
    return 0;
  }

  /**
   * thisYear returns the current year.
   *
   * @return {number}
   */
  @computed
  get thisYear() {
    return moment().year();
  }

  /**
   * nextYear returns the current year, plus one.
   *
   * @return {number}
   */
  @computed('thisYear')
  get nextYear() {
    return this.thisYear + 1;
  }

  @action
  saveLearner(learner) {
    let chain = Promise.resolve();
    let updateEnrolment = false;
    let startDate = '';
    if (learner.learner.enrolment.hasDirtyAttributes) {
      updateEnrolment = true;
      startDate = learner.learner.enrolment.entryDate;
    }
    if (learner.hasDirtyAttributes) {
      chain = learner.save();
    }
    if (updateEnrolment) {
      // Update the enrolment record before saving to ensure that we don't accidentally wipe anything
      // which was updated by the Person update.
      chain.then(() => {
        return learner.learner.enrolment.reload().then((record) => {
          record.set('entryDate', startDate);
          return record.save();
        });
      });
    }

    return chain;
  }

  @action
  setHomeroom(learner, group) {
    let url = `${config.APP.apiHost}/groups/v4/groups/${group.id}/learners:updatemembership`;
    let data = {
      groupId: group.id,
      personId: learner.id,
      status: 1, // MemberStatus.CURRENT = 1
    };
    return this.fetch.post(url, { data }).then(() => {
      if (group.year === this.thisYear) {
        // If we are altering the learners current homeroom, we need to
        // reload the person model as the homeroom id will have changed on
        // the person record.
        learner.reload();
      } else {
        // If we are updating the next year's homeroom, the learner's current
        // homeroom won't change, therefore we only have to do a local store
        // mutation to bind the next year's homeroom against the learner.
        //
        // Note: As ember merges updated data into existing store records,
        // the nextYearsHomeroom property won't disappear if the learner's
        // current homeroom changes and the model reloads.
        learner.set('nextYearsHomeroom', `${group.name} (${this.nextYear})`);
      }

      return learner;
    });
  }

  @action
  toggleLearner(learner, checked) {
    if (checked) {
      this.selected = [...this.selected, learner.id];
    } else {
      this.selected = this.selected.filter((id) => id !== learner.id);
    }
  }

  @action
  selectAll() {
    this.selected = this.ready.map((item) => item.id);
  }

  @action
  deselectAll() {
    this.selected = [];
  }

  @action
  processSelected() {
    this.processing = true;
    this.uploaded = false;
    this.processingDialogOpen = true;
    next(this, 'processEnrolments');
  }

  @action
  closeProcessingDialog() {
    // Trigger an update to remove anyone who is no longer pre-enrolled from the list.
    this.pre.update();
    this.processingDialogOpen = false;
  }

  processEnrolments() {
    this.numProcessed = 0;
    // Fetch Enrolment records (in blocks of 50 to avoid generating URLs which
    // exceed the maximum length) to ensure we have the latest.
    let enrolmentIds = this.selectedLearners.map(
      (item) => item.learner.enrolmentId,
    );
    loadRecordsInChunks(
      this.store,
      'people/enrolment',
      'enrolments',
      enrolmentIds,
      50,
    )
      .then((records) => {
        // Build a promise chain to update each Enrolment record to Current
        // (enrolled) status and jurisdictional Pending Enrolment.
        return records.reduce((chain, record) => {
          return chain
            .then(() => {
              record.set('status', EnrolmentStatus.CURRENT);
              record.set(
                'jurisdictionalStatus',
                JurisdictionalStatus.PENDING_ENROLMENT,
              );
              return record.save();
            })
            .finally(() => {
              this.incrementProperty('numProcessed');
            });
        }, Promise.resolve());
      })
      .then(() => {
        // All applicable Enrolment records have been updated.  Fetch the
        // corresponding Person records (in blocks of 50 to avoid generating URLs
        // which exceed the maximum length).
        loadRecordsInChunks(
          this.store,
          'person',
          'people',
          this.selected,
          50,
        ).then(() => {
          this.processing = false;
        });
      });
  }

  reset() {
    this.selected = [];
    this.processing = false;
    this.processingDialogOpen = false;
    this.numProcessed = 0;
  }
}
